/**
 * React hooks for feature flags.
 * Provides convenient access to feature flag values and utilities in React components.
 */

import React, { useContext, useMemo } from 'react';
import {
  FeatureFlag,
  type UseFeatureFlagResult,
  type UseFeatureFlagsResult,
  type UseSubscriptionFeatureOptions,
  type UseSubscriptionFeatureResult,
} from './types';
import { FeatureFlagContext } from './provider';
import { FEATURE_FLAG_CONFIG } from './config';

/**
 * Hook to access the feature flag context.
 * Throws an error if used outside of FeatureFlagProvider.
 *
 * @returns The feature flag context value
 * @throws Error if used outside of FeatureFlagProvider
 */
function useFeatureFlagContext() {
  const context = useContext(FeatureFlagContext);

  if (!context) {
    throw new Error(
      'useFeatureFlagContext must be used within a FeatureFlagProvider. ' +
        'Make sure your component is wrapped with <FeatureFlagProvider>.'
    );
  }

  return context;
}

/**
 * Hook to check a specific feature flag.
 *
 * @param flag - The feature flag to check
 * @returns Object with flag state and configuration
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isEnabled, config } = useFeatureFlag(FeatureFlag.ENABLE_SUBSCRIPTIONS);
 *
 *   if (!isEnabled) {
 *     return <div>Subscriptions not available</div>;
 *   }
 *
 *   return <SubscriptionFeatures />;
 * }
 * ```
 */
export function useFeatureFlag(flag: FeatureFlag): UseFeatureFlagResult {
  const { isEnabled, isInitialized } = useFeatureFlagContext();

  return useMemo(
    () => ({
      isEnabled: isEnabled(flag),
      config: FEATURE_FLAG_CONFIG[flag],
      isInitialized,
    }),
    [isEnabled, flag, isInitialized]
  );
}

/**
 * Hook to access all feature flags and utilities.
 *
 * @returns Object with all flags, checking functions, and state
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { flags, isEnabled, areEnabled } = useFeatureFlags();
 *
 *   // Check single flag
 *   if (isEnabled(FeatureFlag.ENABLE_SUBSCRIPTIONS)) {
 *     // ...
 *   }
 *
 *   // Check multiple flags
 *   if (areEnabled([FeatureFlag.ENABLE_SUBSCRIPTIONS])) {
 *     // ...
 *   }
 *
 *   return <div>Flags: {JSON.stringify(flags)}</div>;
 * }
 * ```
 */
export function useFeatureFlags(): UseFeatureFlagsResult {
  const context = useFeatureFlagContext();

  return useMemo(
    () => ({
      flags: context.flags,
      isEnabled: context.isEnabled,
      areEnabled: context.areEnabled,
      isAnyEnabled: context.isAnyEnabled,
      isInitialized: context.isInitialized,
    }),
    [context]
  );
}

/**
 * Hook specifically for subscription feature flag with enhanced functionality.
 *
 * @param options - Configuration options for the hook
 * @returns Object with subscription-specific flag state and utilities
 *
 * @example
 * ```tsx
 * function SubscriptionComponent() {
 *   const {
 *     isEnabled,
 *     showSubscriptionUI,
 *     allowSubscriptionActions
 *   } = useSubscriptionFeature();
 *
 *   if (!showSubscriptionUI) {
 *     return null;
 *   }
 *
 *   return (
 *     <div>
 *       <h3>Subscription Plans</h3>
 *       {allowSubscriptionActions && (
 *         <button>Subscribe Now</button>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useSubscriptionFeature(
  options: UseSubscriptionFeatureOptions = {}
): UseSubscriptionFeatureResult {
  const { fallbackValue = false, redirectOnDisabled = false } = options;
  const { isEnabled, isInitialized } = useFeatureFlagContext();
  const [isLoading, setIsLoading] = React.useState(!isInitialized);

  const subscriptionEnabled = isEnabled(FeatureFlag.ENABLE_SUBSCRIPTIONS);

  React.useEffect(() => {
    if (isInitialized) {
      setIsLoading(false);
    }
  }, [isInitialized]);

  // Redirect logic could be implemented here if needed
  React.useEffect(() => {
    if (redirectOnDisabled && !subscriptionEnabled && !isLoading) {
      // This could trigger a redirect - implementation depends on routing solution
      console.warn(
        '[FeatureFlags] Subscription features disabled but redirect requested'
      );
    }
  }, [redirectOnDisabled, subscriptionEnabled, isLoading]);

  const renderSubscriptionContent = React.useCallback(
    (content: React.ReactNode, fallback: React.ReactNode = null) => {
      return subscriptionEnabled || fallbackValue ? content : fallback;
    },
    [subscriptionEnabled, fallbackValue]
  );

  return useMemo(
    () => ({
      isEnabled: subscriptionEnabled || fallbackValue,
      isLoading,
      renderSubscriptionContent,
    }),
    [subscriptionEnabled, fallbackValue, isLoading, renderSubscriptionContent]
  );
}

/**
 * Hook to check multiple feature flags with flexible logic.
 *
 * @param flags - Array of feature flags to check
 * @param requireAll - If true, all flags must be enabled; if false, at least one must be enabled
 * @returns Boolean indicating if the condition is met
 *
 * @example
 * ```tsx
 * function MultiFeatureComponent() {
 *   // All flags must be enabled
 *   const allEnabled = useMultipleFeatureFlags(
 *     [FeatureFlag.ENABLE_SUBSCRIPTIONS],
 *     true
 *   );
 *
 *   // At least one flag must be enabled
 *   const anyEnabled = useMultipleFeatureFlags(
 *     [FeatureFlag.ENABLE_SUBSCRIPTIONS],
 *     false
 *   );
 *
 *   if (!allEnabled) {
 *     return <div>Required features not available</div>;
 *   }
 *
 *   return <div>All features available!</div>;
 * }
 * ```
 */
export function useMultipleFeatureFlags(
  flags: FeatureFlag[],
  requireAll: boolean = true
): boolean {
  const { areEnabled, isAnyEnabled } = useFeatureFlagContext();

  return useMemo(() => {
    return requireAll ? areEnabled(flags) : isAnyEnabled(flags);
  }, [areEnabled, isAnyEnabled, flags, requireAll]);
}

/**
 * Hook to get feature flag configuration without checking the flag value.
 * Useful for accessing flag metadata like descriptions and environment variables.
 *
 * @param flag - The feature flag to get configuration for
 * @returns The flag's configuration object
 *
 * @example
 * ```tsx
 * function FeatureFlagInfo({ flag }: { flag: FeatureFlag }) {
 *   const config = useFeatureFlagConfig(flag);
 *
 *   return (
 *     <div>
 *       <h4>{flag}</h4>
 *       <p>{config.description}</p>
 *       <p>Default: {String(config.defaultValue)}</p>
 *       <p>Environment Variable: {config.envVar}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useFeatureFlagConfig(flag: FeatureFlag) {
  return useMemo(() => FEATURE_FLAG_CONFIG[flag], [flag]);
}

/**
 * Hook to get all feature flag configurations.
 * Useful for building administrative interfaces or debug panels.
 *
 * @returns Object mapping all feature flags to their configurations
 *
 * @example
 * ```tsx
 * function FeatureFlagAdmin() {
 *   const configs = useAllFeatureFlagConfigs();
 *   const { flags } = useFeatureFlags();
 *
 *   return (
 *     <div>
 *       {Object.entries(configs).map(([flag, config]) => (
 *         <div key={flag}>
 *           <h3>{flag}</h3>
 *           <p>Current: {String(flags[flag as FeatureFlag])}</p>
 *           <p>Default: {String(config.defaultValue)}</p>
 *           <p>Description: {config.description}</p>
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAllFeatureFlagConfigs() {
  return useMemo(() => FEATURE_FLAG_CONFIG, []);
}

/**
 * Hook for conditional rendering based on feature flags.
 * Returns render functions that automatically check flag states.
 *
 * @param flag - The feature flag to check
 * @returns Object with conditional render functions
 *
 * @example
 * ```tsx
 * function ConditionalComponent() {
 *   const { renderIfEnabled, renderIfDisabled } = useConditionalRender(
 *     FeatureFlag.ENABLE_SUBSCRIPTIONS
 *   );
 *
 *   return (
 *     <div>
 *       {renderIfEnabled(() => (
 *         <SubscriptionFeatures />
 *       ))}
 *
 *       {renderIfDisabled(() => (
 *         <div>Subscriptions coming soon!</div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useConditionalRender(flag: FeatureFlag) {
  const { isEnabled } = useFeatureFlagContext();
  const flagEnabled = isEnabled(flag);

  return useMemo(
    () => ({
      renderIfEnabled: (render: () => React.ReactNode) =>
        flagEnabled ? render() : null,
      renderIfDisabled: (render: () => React.ReactNode) =>
        !flagEnabled ? render() : null,
      renderConditional: (
        enabledRender: () => React.ReactNode,
        disabledRender: () => React.ReactNode
      ) => (flagEnabled ? enabledRender() : disabledRender()),
    }),
    [flagEnabled]
  );
}

/**
 * Hook to safely access feature flags with fallback values.
 * Useful when you want to provide default behavior if the provider is not available.
 *
 * @param flag - The feature flag to check
 * @param fallback - Fallback value if provider is not available
 * @returns Boolean flag value or fallback
 *
 * @example
 * ```tsx
 * function OptionallyWrappedComponent() {
 *   // Will use fallback if not inside FeatureFlagProvider
 *   const isEnabled = useSafeFeatureFlag(
 *     FeatureFlag.ENABLE_SUBSCRIPTIONS,
 *     false
 *   );
 *
 *   return isEnabled ? <SubscriptionFeatures /> : <div>Not available</div>;
 * }
 * ```
 */
export function useSafeFeatureFlag(
  flag: FeatureFlag,
  fallback: boolean
): boolean {
  const context = useContext(FeatureFlagContext);

  return useMemo(() => {
    if (!context) {
      console.warn(
        `[FeatureFlags] useSafeFeatureFlag(${flag}) used outside of provider, using fallback: ${fallback}`
      );
      return fallback;
    }
    return context.isEnabled(flag);
  }, [context, flag, fallback]);
}
